import React, { useRef, useEffect } from 'react';
import qs from 'qs';
import { useLocation, useHistory } from 'react-router-dom';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { setGridLoadTime } from 'src/slices/app';
// components
import DataGrid from '../DataGrid';
import Card from 'shared/layout/Card';
// helpers
import cleanupPath from 'src/shared/helpers/cleanupPath';
import { useDataGridSettingsCache } from '../../../hooks/useDataGridSettingsCache';

const deepEqual = require('deepequal');

/**
 * PageGrid component.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Function} props.onAdd - Callback function when an item is added.
 * @param {Function} props.onRowSelect - Callback function when a row is selected.
 * @param {boolean} props.didLoad - Indicates whether the component has loaded.
 * @param {Object} props.initialSearchParams - The initial search parameters.
 * @param {Object} props.filteringOptions - The filtering options.
 * @param {boolean} [props.useSearchQuery=true] - Whether to use the search query.
 * @param {boolean} [props.openDetailOnSelect=false] - Whether to open details on select.
 * @param {string} props.className - The class name.
 * @param {boolean} [props.groupable=false] - Whether the component is groupable.
 * @param {Array} props.additionalButtons - Additional buttons to be displayed.
 * @param {boolean} [props.hideActionsCell=false] - Whether to hide the actions cell.
 * @param {boolean} [props.useFilterCache=false] - Whether to use filter cache.
 * @param {boolean} [props.showAsCard=true] - Whether to show as a card.
 * @param {boolean} [props.hideSubTitle=false] - Whether to hide the subtitle.
 * @param {boolean} [props.usePageAdd=true] - Whether to hide the subtitle.
 * @param {boolean} [props.routeOnNew=true] - Whether to push the new location when add row is selected (don't know why this is the default behavior).
 * @param {boolean} [props.logLoadTime={false}] Indicates if display time is logged with Heap. False by default.
 * @property {...import('../DataGrid/DataGrid').DataGridProps} props - Additional props extend data grid.
 */
const PageGrid = ({
  onAdd,
  onRowSelect,
  didLoad,
  initialSearchParams,
  filteringOptions,
  useSearchQuery = true,
  openDetailOnSelect = false,
  className,
  groupable = false,
  additionalButtons,
  hideActionsCell = false,
  useFilterCache = false,
  showAsCard = true,
  hideSubTitle = false,
  isMultiSorting = true,
  usePageAdd = true,
  routeOnNew = true,
  logLoadTime = false,
  showRestoreColumn = true,
  ...props
}) => {
  const dispatch = useDispatch();

  // Reset load time on unmount
  useEffect(() => {
    if (logLoadTime) {
      return () => {
        dispatch(setGridLoadTime(null));
      };
    }
    return () => {};
  }, []);

  const { setGridCache } = useDataGridSettingsCache(useFilterCache);

  const {
    name, subtitle, identifierColumn = 'id', tableMaint,
  } = props;
  const { pathname, search: querySearch } = useLocation();
  const { search: searchValue } = qs.parse(querySearch, { ignoreQueryPrefix: true });
  const gridSearch = useRef();

  const openDetailOnSelectGrid = openDetailOnSelect && !pathname.includes('recycle-bin');

  const gridClassName = cn('page-grid', {
    'selectable-grid': openDetailOnSelectGrid,
    [className]: className,
  });

  const searchParamsPopulated = () => {
    const { Search: defaultSearch, search: defaultSearch1, ...defaultSearchParams } = initialSearchParams || {};
    const { Search: currentSearch, search: currentSearch1, ...currentSearchParams } = props.searchParams || {};

    return !deepEqual(defaultSearchParams, currentSearchParams);
  };

  const Title = () => (
    <div>
      <div>{name}</div>
      {!hideSubTitle && searchValue?.length > 0 && !searchParamsPopulated() && (
        <div className='data-grid-sub-title'>
          Showing search results for
          {' '}
          {searchValue.split(',').map((text, ind) => (
            <span key={text}>
              {ind > 0 && ', '}
              <b>{text}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  const history = useHistory();

  const setCache = (id) => {
    if (gridSearch.current) {
      setGridCache?.(id, gridSearch.current);
    }
  };

  const handleOpenDetail = (selected, e) => {
    if (e?.nativeEvent?.srcElement?.tagName === 'A') { return; }
    const selectedId = selected ? selected[identifierColumn] : 'new';
    if (e?.ctrlKey) {
      window.open(cleanupPath(`${pathname}/${selectedId}`), '_blank');
    } else {
      if (useFilterCache) {
        setCache(selectedId);
      }
      if (!routeOnNew) { return; }
      history.push(cleanupPath(`${pathname}/${selectedId}`));
      if (window?.getSelection && window.getSelection()?.empty) {
        window.getSelection().empty();
      }
    }
  };

  const handleRowSelect = (selected, e) => {
    if (onRowSelect) {
      onRowSelect(selected, e);
      return;
    }

    if (tableMaint) {
      return;
    }

    handleOpenDetail(selected, e);
  };

  const handleDidLoad = (data, { deletedOnly, ...searchParams }) => {
    if (didLoad) {
      didLoad(data, searchParams);
    }

    // Set load time (used in TitledRoutesPage)
    if (logLoadTime) {
      dispatch(setGridLoadTime({ pathname, time: window.performance.now() }));
    }
  };

  const onShowRecycleBin = () => {
    history.push(cleanupPath(`${pathname}/recycle-bin`));
  };

  const onHideRecycleBin = () => {
    history.push(cleanupPath(pathname.replace('/recycle-bin', '')));
  };

  const onSearchChanged = (value) => {
    gridSearch.current = value;
  };

  const handleAdd = !tableMaint && (() => (onAdd ? onAdd() : handleRowSelect()));

  const grid = (
    <DataGrid
      tableMaint={tableMaint}
      isMultiSorting={isMultiSorting}
      title={<Title />}
      subtitle={subtitle}
      hasUnifiedTextSearch
      initialRecycleBin={pathname.endsWith('recycle-bin')}
      onShowRecycleBin={onShowRecycleBin}
      onHideRecycleBin={onHideRecycleBin}
      onRowSelect={openDetailOnSelectGrid ? handleRowSelect : onRowSelect}
      onEnterEdit={handleRowSelect}
      onAdd={handleAdd}
      didLoad={handleDidLoad}
      filteringOptions={filteringOptions}
      initialSearchParams={initialSearchParams}
      useSearchQuery={useSearchQuery}
      searchValue={searchValue}
      className={gridClassName}
      groupable={groupable}
      additionalButtons={additionalButtons}
      hideActionsCell={hideActionsCell && !openDetailOnSelect}
      onSearchChanged={onSearchChanged}
      useFilterCache={useFilterCache}
      usePageAdd={usePageAdd}
      showRestoreColumn={showRestoreColumn}
      {...props}
    />
  );

  return (
    <div>
      {showAsCard ? <Card fluid>{grid}</Card> : grid}
    </div>
  );
};

export default PageGrid;
